// src/pages/admin/candidate-status/index.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, Pen, Plus, Trash2 } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";

import { DataTableNew } from "@/components/shared/DataTable";
import SearchInput from "@/components/shared/SearchInput";
import CustomTooltip from "@/components/shared/CustomTooltip";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import Select from "@/components/ui/Select";
import useLiveResource from "@/hooks/useLiveResource";

import CandidateStatusService, {
  CANDIDATE_STATUS_SORT_FIELDS,
} from "@/services/candidateStatusService";
import { getStatusType } from "./statusFlags";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import AddEditCandidateStatusModal from "./components/AddEditCandidateStatusModal";
import ViewCandidateStatusModal from "./components/ViewCandidateStatusModal";
import DeleteCandidateStatusModal from "./components/DeleteCandidateStatusModal";
import AddEditFlowModal from "./components/AddEditFlowModal";
import FlowSummaryCard from "./components/FlowSummaryCard";
import DeleteFlowModal from "./components/DeleteFlowModal";
import {
  CANDIDATE_STATUS_DEFAULT_SORT,
  CANDIDATE_STATUS_MODAL_MODES,
  CANDIDATE_STATUS_TABLE_SEARCH_PLACEHOLDER,
  DEFAULT_CANDIDATE_STATUS_FORM_VALUES,
  DEFAULT_FLOW_FORM_VALUES,
  FLOW_MODAL_MODES,
} from "./constants";
import CategoryPill from "./components/Categorypill";

const normalizeStatus = (status = {}) => ({
  id: status?.id ?? "",
  code: status?.code ?? "",
  name: status?.name ?? "",
  description: status?.description ?? "",
  sequence: status?.sequence ?? 0,
  isInitial: !!status?.isInitial,
  isHired: !!status?.isHired,
  isRejected: !!status?.isRejected,
  isReusable: !!status?.isReusable,
  isInterviewEligible: !!status?.isInterviewEligible,
  allowedTransitionIds: Array.isArray(status?.allowedTransitionIds)
    ? status.allowedTransitionIds
    : [],
  inUseCount: status?.inUseCount ?? status?.in_use_count ?? 0,
});

const buildPayload = (values = {}, flowId) => ({
  flowId,
  name: values?.name?.trim(),
  description: values?.description?.trim(),
  sequence: values?.sequence ?? undefined,
  isInitial: !!values?.isInitial,
  isHired: !!values?.isHired,
  isRejected: !!values?.isRejected,
  isReusable: !!values?.isReusable,
  isInterviewEligible: !!values?.isInterviewEligible,
  // Hired and Rejected are both terminal — the server refuses an outgoing
  // edge from either, so neither posts one.
  allowedTransitionIds: values?.isRejected || values?.isHired
    ? []
    : Array.isArray(values?.allowedTransitionIds)
      ? values.allowedTransitionIds
      : [],
});

// The Yes/No chip the Interview Scheduling column wears. Same rounded-full
// 11px shape as CategoryPill, so the two read as one table.
const StatusBooleanChip = ({ value, trueLabel = "Yes", falseLabel = "No" }) => (
  <span
    className={[
      "inline-flex items-center justify-center rounded-full px-2 py-1 text-[11px] font-medium leading-none",
      value
        ? "bg-success/10 text-success"
        // A bordered, filled chip in both themes. A plain `bg-background-form`
        // sat on a near-identical cell colour in light mode (and the exact
        // same one in dark), so the "No" chips lost their shape and read as
        // bare text.
        : "border border-border-primary bg-background-tertiary text-text-secondary",
    ].join(" ")}
  >
    {value ? trueLabel : falseLabel}
  </span>
);

const normalizeFlow = (flow = {}) => ({
  id: flow?.id ?? "",
  name: flow?.name ?? "",
  description: flow?.description ?? "",
  isDefault: !!flow?.isDefault,
  isActive: flow?.isActive ?? true,
  statusCount: flow?.statusCount ?? 0,
  // True once the flow has an Initial, a Hired and a Rejected status and
  // every non-terminal status has at least one allowed transition out of
  // it — isReusable is not part of this, it's optional. A job can't be
  // created or edited onto a flow until this is true (FLOW_NOT_READY).
  isReady: !!flow?.isReady,
});

const CandidateStatusPage = () => {
  const setHeader = useOutletContext();

  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [tableData, setTableData] = useState({ total: 0, isLoading: false, totalActive: 0, maxLimit: 15 });

  const [activeModal, setActiveModal] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [deletingStatus, setDeletingStatus] = useState(null);
  const [statusList, setStatusList] = useState([]);

  // Which of the tenant's flows this page is currently managing. Every
  // status list/create/edit/delete call below is scoped to it.
  const [flows, setFlows] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(true);
  const [flowLimit, setFlowLimit] = useState(10);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [flowModal, setFlowModal] = useState(null);
  const [editingFlow, setEditingFlow] = useState(null);
  const [deletingFlow, setDeletingFlow] = useState(null);

  // Which of Initial/Hired/Rejected the selected flow has, for the
  // not-ready warning below. The table's own fetch is paginated and
  // search-filtered, so it can't answer this — fetched separately with
  // per_page:100 the same way AddEditCandidateStatusModal loads every
  // status in a flow (the flow's status cap is 15, so 100 always covers it).
  const [requiredTypePresence, setRequiredTypePresence] = useState({
    hasInitial: false,
    hasHired: false,
    hasRejected: false,
    deadEnds: [],
  });

  useEffect(() => {
    setHeader({
      title: "Candidate Status",
      subTitle: "Pipelines a job's candidates move through",
    });

    return () =>
      setHeader({
        title: "",
        subTitle: "",
      });
  }, [setHeader]);

  const triggerRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const loadFlows = useCallback(async (preferFlowId) => {
    try {
      setFlowsLoading(true);
      const res = await CandidateStatusService.listFlows({ per_page: 100 });
      const rawList = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
      const list = rawList.map(normalizeFlow);
      setFlows(list);
      setFlowLimit(res?.maxLimit ?? res?.max_limit ?? 10);

      setSelectedFlowId((current) => {
        const wanted = preferFlowId ?? current;
        if (wanted && list.some((f) => f.id === wanted)) return wanted;
        return list.find((f) => f.isDefault)?.id ?? list[0]?.id ?? null;
      });
    } catch (e) {
      toast.error(apiErrorMessageFormat(e, "Failed to load status flows"));
    } finally {
      setFlowsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRequiredTypePresence = useCallback(async (flowId) => {
    if (!flowId) {
      setRequiredTypePresence({
        hasInitial: false,
        hasHired: false,
        hasRejected: false,
        deadEnds: [],
      });
      return;
    }
    try {
      const res = await CandidateStatusService.list({
        flowId,
        page: 1,
        per_page: 100,
        sort_by: "sequence",
        sort_order: "asc",
      });
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
      setRequiredTypePresence({
        hasInitial: list.some((s) => s?.isInitial),
        hasHired: list.some((s) => s?.isHired),
        hasRejected: list.some((s) => s?.isRejected),
        // The fourth thing readiness asks for, and the one a finished-looking
        // list screen hides: a non-terminal status with nowhere to go traps
        // every candidate who reaches it. Mirrors the server's
        // repository.statuses_without_outgoing_transitions.
        deadEnds: list
          .filter(
            (s) =>
              !s?.isHired &&
              !s?.isRejected &&
              !(Array.isArray(s?.allowedTransitionIds) && s.allowedTransitionIds.length)
          )
          .map((s) => s?.name)
          .filter(Boolean),
      });
    } catch (e) {
      // The not-ready warning still shows off selectedFlow.isReady either
      // way — this only feeds which checkmarks it draws, so a failed fetch
      // here is silent rather than a second error toast on the page.
    }
  }, []);

  useEffect(() => {
    loadRequiredTypePresence(selectedFlowId);
  }, [selectedFlowId, refreshKey, loadRequiredTypePresence]);

  // `candidate_status.changed` is the tenant's list of statuses being edited —
  // not an application moving between them, which is
  // `application.status_changed` and belongs to five other screens. The flow
  // endpoints emit the same event: a flow being renamed, retired or deleted
  // leaves this screen holding a stale flow picker as surely as a status edit
  // leaves it holding a stale table, and both are re-read here.
  //
  // No toast: the envelope carries neither an actor nor a message, and the
  // list visibly changing under the reader is the signal.
  useLiveResource({
    keys: "candidate_status",
    onChange: () => {
      triggerRefresh();
      loadFlows();
    },
  });

  const fetchCandidateStatuses = useCallback(async (params = {}) => {
    if (!selectedFlowId) {
      setTableData((prev) => ({ ...prev, totalActive: 0, maxLimit: 15 }));
      return { data: { statuses: [], pagination: { total: 0 }, totalActive: 0, maxLimit: 15 } };
    }

    const res = await CandidateStatusService.list({
      flowId: selectedFlowId,
      page: params.page ?? 1,
      per_page: params.per_page ?? 10,
      sort_by: CANDIDATE_STATUS_SORT_FIELDS.includes(params.sort_by)
        ? params.sort_by
        : "sequence",
      sort_order: params.sort_order ?? "asc",
    });

    const rawData = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];

    const rawPagination =
      res?.pagination ??
      res?.data?.pagination ??
      {};

    const list = rawData.map(normalizeStatus);
    const totalActive = res?.totalActive ?? res?.total_active ?? list.length;
    const maxLimit = res?.maxLimit ?? res?.max_limit ?? 15;

    // DataTableNew's onDataFetch only ever forwards { total, isLoading } —
    // it doesn't pass through the extra fields on this API's response — so
    // totalActive/maxLimit are captured here instead, straight off the
    // response, rather than relying on onDataFetch to carry them.
    setTableData((prev) => ({ ...prev, totalActive, maxLimit }));
    setStatusList(list);

    return {
      data: {
        statuses: list,
        pagination: {
          total: rawPagination?.total ?? list.length,
          has_next: rawPagination?.has_next ?? rawPagination?.hasNext ?? false,
          has_prev: rawPagination?.has_prev ?? rawPagination?.hasPrev ?? false,
          page: rawPagination?.page ?? params.page ?? 1,
          per_page: rawPagination?.per_page ?? rawPagination?.perPage ?? params.per_page ?? 10,
          total_pages:
            rawPagination?.total_pages ??
            rawPagination?.totalPages ??
            Math.ceil((rawPagination?.total ?? list.length) / (rawPagination?.per_page ?? rawPagination?.perPage ?? params.per_page ?? 10)),
        },
        totalActive,
        maxLimit,
      },
    };
  }, [selectedFlowId]);

  const onDataFetch = useCallback((data) => {
    setTableData((prev) => ({
      ...prev,
      total: data?.total ?? 0,
      isLoading: !!data?.isLoading,
    }));
  }, []);

  const closeAll = useCallback(() => {
    setActiveModal(null);
    setSelectedStatus(null);
    setDeletingStatus(null);
  }, []);

  // ── Flow CRUD ────────────────────────────────────────────────────────────

  const closeFlowModal = useCallback(() => {
    setFlowModal(null);
    setEditingFlow(null);
    setDeletingFlow(null);
  }, []);

  const handleOpenAddFlow = useCallback(() => {
    setEditingFlow(null);
    setFlowModal(FLOW_MODAL_MODES.ADD);
  }, []);

  const handleOpenEditFlow = useCallback((flow) => {
    setEditingFlow(flow);
    setFlowModal(FLOW_MODAL_MODES.EDIT);
  }, []);

  const handleOpenDeleteFlow = useCallback((flow) => {
    setDeletingFlow(flow);
    setFlowModal(FLOW_MODAL_MODES.DELETE);
  }, []);

  const handleSaveFlow = useCallback(
    async (values) => {
      const payload = { name: values?.name?.trim(), description: values?.description?.trim() || null };
      // isActive only makes sense as an update, and never for the Default
      // flow (the modal hides the control for it, but guard here too).
      if (flowModal === FLOW_MODAL_MODES.EDIT && !editingFlow?.isDefault) {
        payload.isActive = !!values?.isActive;
      }

      if (flowModal === FLOW_MODAL_MODES.EDIT && editingFlow?.id) {
        const res = await CandidateStatusService.updateFlow(editingFlow.id, payload);
        toast.success("Flow updated");
        closeFlowModal();
        await loadFlows(res?.data?.id ?? editingFlow.id);
      } else {
        const res = await CandidateStatusService.createFlow(payload);
        toast.success("Flow created");
        closeFlowModal();
        await loadFlows(res?.data?.id);
      }
    },
    [flowModal, editingFlow, closeFlowModal, loadFlows]
  );

  const handleDeleteFlow = useCallback(async () => {
    await CandidateStatusService.deleteFlow(deletingFlow.id);
    toast.success("Flow deleted");
    closeFlowModal();
    await loadFlows();
  }, [deletingFlow, closeFlowModal, loadFlows]);

  const selectedFlow = useMemo(
    () => flows.find((f) => f.id === selectedFlowId) ?? null,
    [flows, selectedFlowId]
  );

  const statusLimitReached = tableData.totalActive >= tableData.maxLimit;

  const flowOptions = useMemo(
    () =>
      flows.map((f) => ({
        label: f.isDefault
          ? `${f.name} (Default)`
          : f.isActive
            ? f.name
            : `${f.name} (Inactive)`,
        value: f.id,
      })),
    [flows]
  );

  const defaultValuesForFlowModal = useMemo(() => {
    if (flowModal === FLOW_MODAL_MODES.EDIT && editingFlow) {
      return {
        name: editingFlow.name,
        description: editingFlow.description,
        isActive: editingFlow.isActive ?? true,
      };
    }
    return DEFAULT_FLOW_FORM_VALUES;
  }, [flowModal, editingFlow]);

  const handleOpenAdd = useCallback(() => {
    setSelectedStatus(null);
    setActiveModal(CANDIDATE_STATUS_MODAL_MODES.ADD);
  }, []);

  const handleOpenView = useCallback(async (row) => {
    try {
      const res = await CandidateStatusService.getById(row?.id, selectedFlowId);
      setSelectedStatus(normalizeStatus(res?.data));
      setActiveModal(CANDIDATE_STATUS_MODAL_MODES.VIEW);
    } catch (e) {
      toast.error(apiErrorMessageFormat(e, "Failed to fetch candidate status"));
    }
  }, [selectedFlowId]);

  const handleOpenEdit = useCallback(async (row) => {
    try {
      const res = await CandidateStatusService.getById(row?.id, selectedFlowId);
      setSelectedStatus(normalizeStatus(res?.data));
      setActiveModal(CANDIDATE_STATUS_MODAL_MODES.EDIT);
    } catch (e) {
      toast.error(apiErrorMessageFormat(e, "Failed to fetch candidate status"));
    }
  }, [selectedFlowId]);

  const handleOpenDelete = useCallback((row) => {
    setDeletingStatus(row);
    setActiveModal(CANDIDATE_STATUS_MODAL_MODES.DELETE);
  }, []);

  const handleSaveStatus = useCallback(
    async (values) => {
      try {
        const payload = buildPayload(values, selectedFlowId);

        if (
          activeModal === CANDIDATE_STATUS_MODAL_MODES.EDIT &&
          selectedStatus?.id
        ) {
          await CandidateStatusService.update(selectedStatus.id, payload, selectedFlowId);
          toast.success("Candidate status updated");
        } else {
          await CandidateStatusService.create(payload);
          toast.success("Candidate status created");
        }

        closeAll();
        triggerRefresh();
      } catch (e) {
        toast.error(
          apiErrorMessageFormat(
            e,
            `Failed to ${activeModal === CANDIDATE_STATUS_MODAL_MODES.EDIT
              ? "update"
              : "create"
            } candidate status`
          )
        );
      }
    },
    [activeModal, selectedStatus, selectedFlowId, closeAll, triggerRefresh]
  );

  const handleDeleteStatus = useCallback(
    async (replacementStatusId = null) => {
      try {
        await CandidateStatusService.delete(
          deletingStatus?.id,
          replacementStatusId,
          selectedFlowId
        );
        toast.success("Candidate status deleted");
        closeAll();
        triggerRefresh();
      } catch (e) {
        throw e;
      }
    },
    [deletingStatus, selectedFlowId, closeAll, triggerRefresh]
  );

  const defaultValuesForModal = useMemo(() => {
    if (
      activeModal === CANDIDATE_STATUS_MODAL_MODES.EDIT &&
      selectedStatus
    ) {
      return {
        name: selectedStatus?.name ?? "",
        description: selectedStatus?.description ?? "",
        sequence: selectedStatus?.sequence ?? null,
        isInitial: !!selectedStatus?.isInitial,
        isHired: !!selectedStatus?.isHired,
        isRejected: !!selectedStatus?.isRejected,
        isReusable: !!selectedStatus?.isReusable,
        isInterviewEligible: !!selectedStatus?.isInterviewEligible,
        allowedTransitionIds: selectedStatus?.allowedTransitionIds ?? [],
      };
    }

    return DEFAULT_CANDIDATE_STATUS_FORM_VALUES;
  }, [activeModal, selectedStatus]);

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: "Name",
        sortable: true,
        align: "left",
        render: (val) => (
          <span className="text-left font-medium text-[13px]">
            {val || "—"}
          </span>
        ),
      },
      {
        key: "sequence",
        label: "Step",
        sortable: true,
        align: "center",
        render: (val) => (
          <span className="text-center font-medium text-[12px] tabular-nums">
            {val ?? "—"}
          </span>
        ),
      },
      {
        key: "description",
        label: "Description",
        sortable: false,
        align: "left",
        wrap: true,
        style: { width: "30%" },
        render: (val) => (
          <span className="text-left font-medium text-[13px]">
            {val || "—"}
          </span>
        ),
      },
      {
        key: "type",
        label: "Type",
        sortable: false,
        align: "left",
        // Initial/Hired/Rejected/Reusable are mutually exclusive by
        // construction (AddEditCandidateStatusModal's toggles clear the other
        // three the moment one is set) and Initial/Hired/Rejected always take
        // precedence over Shortlisted/Interview too, so this pill already
        // carries all four exclusive-role flags with nothing hidden — except
        // Reusable, which the same precedence can push behind a Shortlisted or
        // Interview pill. A second, smaller pill covers exactly that gap.
        render: (_val, row) => {
          const category = getStatusType(row);
          return (
            <div className="flex flex-wrap items-center gap-1.5">
              <CategoryPill category={category} />
              {row?.isReusable && category !== "REUSABLE" ? (
                <CategoryPill category="REUSABLE" />
              ) : null}
            </div>
          );
        },
      },
      {
        key: "isInterviewEligible",
        label: "Interview Scheduling",
        sortable: false,
        align: "center",
        render: (val) => <StatusBooleanChip value={val} />,
      },
      {
        key: "allowedTransitionIds",
        label: "Transitions",
        sortable: false,
        align: "left",
        render: (val, row) => {
          if (row?.isRejected || row?.isHired) {
            return (
              <span className="text-left font-medium text-[13px] text-text-secondary">
                Terminal
              </span>
            );
          }

          const count = Array.isArray(val) ? val.length : 0;

          return (
            <span className="text-left font-medium text-[13px]">
              {count === 0 ? "No transition" : `${count} status${count === 1 ? "" : "es"}`}
            </span>
          );
        },
      },
      {
        key: "id",
        label: "Actions",
        sortable: false,
        align: "center",
        render: (_val, row) => (
          <div className="flex gap-2 justify-center">
            <CustomTooltip text="View Status">
              <Button
                onClick={(event) => {
                  event.currentTarget.blur();
                  handleOpenView(row);
                }}
                className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60"
              >
                <Eye size={16} />
              </Button>
            </CustomTooltip>

            <CustomTooltip text="Edit Status">
              <Button
                onClick={(event) => {
                  event.currentTarget.blur();
                  handleOpenEdit(row);
                }}
                className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60"
              >
                <Pen size={16} />
              </Button>
            </CustomTooltip>

            <CustomTooltip text="Delete Status">
              <Button
                onClick={(event) => {
                  event.currentTarget.blur();
                  handleOpenDelete(row);
                }}
                className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60"
              >
                <Trash2 size={16} />
              </Button>
            </CustomTooltip>
          </div>
        ),
      },
    ],
    [handleOpenView, handleOpenEdit, handleOpenDelete]
  );

  return (
    <div className="flex flex-col gap-2.5" style={{ zoom: 0.85 }}>
      <FlowSummaryCard
        flowOptions={flowOptions}
        flowsLoading={flowsLoading}
        selectedFlow={selectedFlow}
        selectedFlowId={selectedFlowId}
        onSelectFlow={setSelectedFlowId}
        flowCount={flows.length}
        flowLimit={flowLimit}
        statusCount={tableData.totalActive}
        statusLimit={tableData.maxLimit}
        statusesLoading={tableData.isLoading}
        onAddFlow={handleOpenAddFlow}
        onEditFlow={handleOpenEditFlow}
        onDeleteFlow={handleOpenDeleteFlow}
        requiredTypePresence={requiredTypePresence}
      />

      <div className="flex flex-col md:flex-row md:items-center gap-2.5">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={CANDIDATE_STATUS_TABLE_SEARCH_PLACEHOLDER}
          />
        </div>

        <CustomTooltip text={statusLimitReached ? "This flow is at its status limit" : ""}>
          <Button
            onClick={handleOpenAdd}
            disabled={!selectedFlowId || statusLimitReached}
            className="hidden md:flex bg-button-secondary text-text-base rounded-full px-3 py-2 items-center gap-1 text-[11px] whitespace-nowrap shrink-0 disabled:opacity-50"
          >
            <Plus size={11} />
            Add Status
          </Button>
        </CustomTooltip>
      </div>

      <Card className="p-0 bg-table-heading border-border-primary">
        <DataTableNew
          key={`candidate-status-list-${selectedFlowId}-${refreshKey}`}
          className="rounded-none [&_tbody_tr:hover>td]:bg-table-heading [&_tbody_tr:focus-within>td]:bg-table-heading"
          columns={columns}
          showPagination={true}
          sortable={true}
          api={fetchCandidateStatuses}
          paginationLabel="statuses"
          search={search}
          dataPath="statuses"
          onDataFetch={onDataFetch}
          defaultSort={CANDIDATE_STATUS_DEFAULT_SORT}
        />
      </Card>

      <div className="fixed bottom-0 left-0 right-0 z-[110] md:hidden px-4 py-3">
        <Button
          onClick={handleOpenAdd}
          disabled={!selectedFlowId || statusLimitReached}
          className="w-full bg-button-secondary text-text-base rounded-full py-2.5 flex items-center justify-center gap-1.5 text-[13px] disabled:opacity-50"
        >
          <Plus size={14} />
          Add Status
        </Button>
      </div>

      <AddEditCandidateStatusModal
        isOpen={
          activeModal === CANDIDATE_STATUS_MODAL_MODES.ADD ||
          activeModal === CANDIDATE_STATUS_MODAL_MODES.EDIT
        }
        mode={
          activeModal === CANDIDATE_STATUS_MODAL_MODES.EDIT ? "edit" : "add"
        }
        defaultValues={defaultValuesForModal}
        selectedStatus={selectedStatus}
        flowId={selectedFlowId}
        statusList={statusList}
        onModelClose={closeAll}
        onHandleSubmit={handleSaveStatus}
      />

      <ViewCandidateStatusModal
        open={activeModal === CANDIDATE_STATUS_MODAL_MODES.VIEW}
        onModelClose={closeAll}
        status={selectedStatus}
        flowId={selectedFlowId}
      />

      <DeleteCandidateStatusModal
        open={activeModal === CANDIDATE_STATUS_MODAL_MODES.DELETE}
        onModelClose={closeAll}
        status={deletingStatus}
        flowId={selectedFlowId}
        onConfirm={handleDeleteStatus}
      />

      <AddEditFlowModal
        isOpen={flowModal === FLOW_MODAL_MODES.ADD || flowModal === FLOW_MODAL_MODES.EDIT}
        mode={flowModal === FLOW_MODAL_MODES.EDIT ? "edit" : "add"}
        defaultValues={defaultValuesForFlowModal}
        flow={editingFlow}
        onModelClose={closeFlowModal}
        onHandleSubmit={handleSaveFlow}
      />

      <DeleteFlowModal
        open={flowModal === FLOW_MODAL_MODES.DELETE}
        onModelClose={closeFlowModal}
        flow={deletingFlow}
        onConfirm={handleDeleteFlow}
      />
    </div>
  );
};

export default CandidateStatusPage;
